import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import Lenis from "lenis"
import { useCallback, useEffect, useRef } from "react"

import { EVENTS } from "../const"

gsap.registerPlugin(ScrollTrigger)

export function useAppLenis(
  _experience: any,
  onUpdate?: (lenis: Lenis) => void,
) {
  const experience = useRef<any>(_experience)
  const lenisRef = useRef<Lenis | null>(null)
  const updateHandlerRef = useRef<((lenis: Lenis) => void) | null>(null)

  const updateScrollInfo = useCallback(
    (lenis: Lenis) => {
      const { actualScroll, isScrolling, animatedScroll } = lenis

      const currentScrollInfo = experience.current.scrollInfo || {}
      const previousStartingScroll =
        experience.current.scrollInfo?.startingScroll || 0

      experience.current.scrollInfo = {
        ...currentScrollInfo,
        startingScroll: isScrolling ? previousStartingScroll : actualScroll,
        actualScroll,
        animatedScroll,
        isScrolling,
      }

      onUpdate?.(lenis)
    },
    [onUpdate],
  )

  const createUpdateHandler = useCallback(() => {
    return (lenis: Lenis) => {
      ScrollTrigger.update()
      updateScrollInfo(lenis)
    }
  }, [updateScrollInfo])

  useEffect(() => {
    if (lenisRef.current) return

    const lenis = new Lenis({
      allowNestedScroll: true,
    })
    // lenis.stop()

    lenisRef.current = lenis
    experience.current.lenis = lenis

    experience.current.trigger(EVENTS.Lenis.Initialized)

    gsap.ticker.add((time: number) => {
      lenis.raf(time * 1000)
    })

    // Disable lag smoothing in GSAP to prevent any delay in scroll animations
    gsap.ticker.lagSmoothing(0)

    return () => {
      gsap.ticker.remove(updateHandlerRef.current!)
      lenis.destroy()
      lenisRef.current = null
    }
  }, [])

  // Update scroll handler when dependencies change
  useEffect(() => {
    if (!lenisRef.current) return

    const lenis = lenisRef.current

    // Remove old handler if it exists
    if (updateHandlerRef.current) {
      lenis.off("scroll", updateHandlerRef.current)
    }

    // Create and set new handler
    const newUpdateHandler = createUpdateHandler()
    updateHandlerRef.current = newUpdateHandler
    lenis.on("scroll", newUpdateHandler)
  }, [createUpdateHandler])
}
